import { CommunityWins } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { CommunityWin } from '@platform/client/schemas';
import { useCallback, useMemo, useState, useContext } from 'react';
import { CreateCommunityWinModal } from './CreateCommunityWinModal';
import { DeleteCommunityWinModal } from './DeleteCommunityWinModal';
import { UpdateCommunityWinModal } from './UpdateCommunityWinModal';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import { AdminTable, AdminTableContext } from '../../AdminTable';

const columnHelper = createColumnHelper<CommunityWin>();

const CommunityWinsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <CommunityWinsAdminContent />
    </AdminListPageWrapper>
  );
};

const CommunityWinsAdminContent = () => {
  const { adminQueryOptions, setPagination } = useContext(AdminTableContext);
  const [isCreateCommunityWinOpen, setIsCreateCommunityWinOpen] = useState(false);
  const [openCommunityWin, setOpenCommunityWin] = useState<CommunityWin | undefined>();
  const [communityWinToDelete, setCommunityWinToDelete] = useState<number | undefined>(undefined);
  const { data: communityWinCount = adminQueryOptions.page } =
    CommunityWins.useCountCommunityWins();

  const {
    data: communityWins = [],
    isLoading,
    isError,
    isSuccess,
  } = CommunityWins.useGetCommunityWins(
    {
      page: adminQueryOptions.page,
      limit: adminQueryOptions.limit,
      sort: adminQueryOptions.sort,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const openCommunityWinModal = useCallback(
    (communityWinId: number) => {
      setOpenCommunityWin(communityWins.find((communityWin) => communityWin.id === communityWinId));
    },
    [communityWins],
  );

  const openDeleteCommunityWinModal = (communityWinId: number) => {
    setCommunityWinToDelete(communityWinId);
  };

  const closeCommunityWinModal = () => {
    setOpenCommunityWin(undefined);
    setIsCreateCommunityWinOpen(false);
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (id) => id.getValue(),
          header: () => <span>Id</span>,
          size: 50,
          enableSorting: true,
        }),
        columnHelper.accessor('title', {
          cell: (title) => title.getValue(),
          header: () => <span>Title</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('description', {
          cell: (description) => {
            let descriptionText = description.getValue();
            if (descriptionText.length > 150) {
              descriptionText = description.getValue().substring(0, 150) + '...';
            }
            return descriptionText;
          },
          header: () => 'Description',
          size: 350,
          maxSize: 500,
          enableSorting: false,
        }),
        columnHelper.accessor('link', {
          cell: (info) => <div className='text-wrap w-[150px] break-words'>{info.getValue()}</div>,
          header: () => <span>Link</span>,
          size: 150,
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span>Created At</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.display({
          id: 'Actions',
          header: () => <span>Actions</span>,
          size: 25,
          cell: ({ row }) => (
            <AdminActionsContainer
              actions={[
                {
                  text: 'Edit',
                  type: 'button',
                  onPress: () => openCommunityWinModal(row.original.id),
                },
                {
                  text: 'Delete',
                  type: 'button',
                  onPress: () => openDeleteCommunityWinModal(row.original.id),
                  linkColor: 'red',
                },
              ]}
            />
          ),
        }),
      ] as ColumnDef<CommunityWin, unknown>[],
    [openCommunityWinModal],
  );

  return (
    <>
      <AdminTable
        data={communityWins}
        columns={columns}
        filteredCount={communityWinCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        tableActions={[
          {
            callback: () => setIsCreateCommunityWinOpen(true),
            text: 'Create community win',
          },
        ]}
      />
      <CreateCommunityWinModal
        isOpen={isCreateCommunityWinOpen}
        onClose={closeCommunityWinModal}
        resetPagination={() =>
          setPagination({ pageIndex: adminQueryOptions.page, pageSize: adminQueryOptions.limit })
        }
        limit={adminQueryOptions.limit}
      />
      {communityWinToDelete && (
        <DeleteCommunityWinModal
          isOpen={!!communityWinToDelete}
          onClose={() => setCommunityWinToDelete(undefined)}
          id={communityWinToDelete}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
      {openCommunityWin && (
        <UpdateCommunityWinModal
          communityWin={openCommunityWin}
          isOpen={!!openCommunityWin}
          onClose={closeCommunityWinModal}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
    </>
  );
};

export default CommunityWinsAdmin;
